import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { SizeSwitcherDropdown } from "@components/commerce/product-listing/filters/common/size-switcher/SizeSwitcher";
import { SizeGuide } from "@components/ui/size-guide-modal/SizeGuide";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { getSizeFacetKey } from "@lib/constants/filtersConstants";
import type { SizeLocales } from "@lib/constants/filtersConstants";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { scrollToTopAfterFilter } from "@lib/utils/scrollToTop";
import { isSizeAvailableAtSelectedStores } from "@lib/utils/plp/storeSizesFilter";
import { useFilterStore } from "@store/filterStore";
import { useStore } from "@store/index";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { SelectDropdownItem } from "@ecco/ui/select-dropdown";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { type Dispatch, memo, type SetStateAction, useCallback, use, useEffect } from "react";
import { useRefinementList } from "react-instantsearch";
import { styles } from "./SizeToolbarFilterStyles";

export const SizeToolbarFilter = memo(
    ({
        filterName,
        isToolbar,
        isLoading,
    }: {
        filterName: string;
        isToolbar: boolean;
        isLoading?: boolean;
    }) => {
        // Static data from main store
        const initialFilters = useStore((state) => state.data.products?.initialFilters);
        const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);
        const defaultShoeSize = useStore((state) => state.data.me.globalShoeSizeConvention);
        const setDefaultShoeSize = useStore((state) => state.setGlobalShoeSizeConvention);

        // Dynamic filter store actions (no state subscriptions to avoid rerenders)
        const setFilterItems = useFilterStore((state) => state.setFilterItems);
        const { sizeValuesForSelectedStores } = use(FiltersContext);

        // Algolia refinement hook
        const { items, refine } = useRefinementList({
            attribute: `fullSizes.${getSizeFacetKey(defaultShoeSize)}`,
            operator: "or",
            sortBy: ["name"],
            limit: 100,
        });

        // Update store with current facet data whenever items or refine function changes
        useEffect(() => {
            // Get current facets from store without subscription
            const currentFacets = useFilterStore.getState().facets;

            // Update store with new facet data
            setFilterItems({
                ...currentFacets,
                [filterName]: { items, refine },
            });
        }, [items, refine, filterName, setFilterItems]);

        let timerId = undefined;

        const applyRefinement = useCallback(
            (item: string) => {
                if (timerId) {
                    clearTimeout(timerId);
                } else {
                    timerId = setTimeout(() => {
                        AnalyticsClient.listFilterApplied(
                            filterName,
                            item,
                            `fullSizes.${getSizeFacetKey(defaultShoeSize)}`
                        );
                    }, 1000);
                }
                setLastLoadedPage(1);
                refine(item);
                scrollToTopAfterFilter();
            },
            [refine, filterName, defaultShoeSize, setLastLoadedPage]
        );

        const switchSizeLocale = useCallback(
            (newSizeLocale: SizeLocales) => {
                items.filter((it) => it.isRefined).forEach((it) => refine(it.value));
                setLastLoadedPage(1);
                setDefaultShoeSize(newSizeLocale);
                scrollToTopAfterFilter();
            },
            [items, refine, setLastLoadedPage, setDefaultShoeSize]
        );

        if (!isToolbar) {
            return null;
        }

        const sizesList =
            (initialFilters?.[`Size.${defaultShoeSize}`] as RefinementListItem[]) ?? [];
        const dropdownItems = (
            <Box sx={styles.itemsWrapper}>
                <SizeSection
                    sizesList={sizesList}
                    setSizeLocale={switchSizeLocale}
                    sizeLocale={defaultShoeSize}
                    items={items}
                    refine={applyRefinement}
                    sizeValuesForSelectedStores={sizeValuesForSelectedStores}
                />
                <Flex width="96%" margin="auto" justifyContent={"flex-start"}>
                    <SizeGuide isToolbar={true} type={ModalTypes.globalSizeGuide} />
                </Flex>
            </Box>
        );

        return (
            <FilterBase
                isLoading={isLoading}
                dropdownItems={dropdownItems}
                filterName={filterName}
                isToolbar={isToolbar}
            />
        );
    }
);

SizeToolbarFilter.displayName = "SizeToolbarFilter";

interface SizeSectionProps {
    sizesList?: RefinementListItem[];
    items: RefinementListItem[];
    refine: (value: string) => void;
    sizeLocale: SizeLocales;
    setSizeLocale: Dispatch<SetStateAction<SizeLocales>>;
    sizeValuesForSelectedStores: Set<string> | null;
}

const SizeSection = memo(
    ({
        sizesList = [],
        items,
        refine,
        sizeLocale,
        setSizeLocale,
        sizeValuesForSelectedStores,
    }: SizeSectionProps) => {
        return (
            <Flex
                sx={{
                    ...styles.sizeSectionMainContainer,
                }}
            >
                {sizesList.length ? (
                    <Box sx={styles.sectionWrapper}>
                        <SizeSwitcherDropdown
                            sizeLocale={sizeLocale}
                            setSizeLocale={(label) => {
                                setSizeLocale(label);
                            }}
                        />
                        <Box
                            sx={styles.sizesContainer}
                            overflowY="auto"
                            maxHeight={["70dvh", "63vh"]}
                        >
                            {[...sizesList]
                                .sort((a, b) => {
                                    return parseInt(a.label) - parseInt(b.label);
                                })
                                .map((item) => {
                                    const dynamicItem = items.find((it) => it.value === item.value);
                                    const isAvailable =
                                        !!dynamicItem &&
                                        isSizeAvailableAtSelectedStores(
                                            item.value,
                                            sizeValuesForSelectedStores
                                        );
                                    return (
                                        <SelectDropdownItem
                                            key={item.label}
                                            as={Box}
                                            sx={{
                                                ...styles.selectDropdownItem,
                                            }}
                                            isDisabled={!isAvailable}
                                            onClick={() => {
                                                if (!isAvailable) return;
                                                refine(item.value);
                                            }}
                                        >
                                            <Button
                                                key={item.label}
                                                sx={{
                                                    ...styles.itemButton,
                                                    _disabled: {
                                                        bg: "rgba(238, 238, 238, .3)",
                                                        color: "rgba(0, 0, 0, .6)",
                                                    },
                                                }}
                                                size="sm"
                                                fontWeight="bold"
                                                isDisabled={!isAvailable}
                                                variant={
                                                    dynamicItem?.isRefined ? "solid" : "standard"
                                                }
                                            >
                                                <span>{item.label}</span>
                                            </Button>
                                        </SelectDropdownItem>
                                    );
                                })}
                        </Box>
                    </Box>
                ) : null}
            </Flex>
        );
    }
);

SizeSection.displayName = "SizeSection";
